import { Box } from "@mantine/core";
import { Fragment, useLayoutEffect, useRef, useState } from "react";

import { colors } from "#app/theme/v2/colors";
import { shadows } from "#app/theme/v2/shadows";
import { spacing } from "#app/theme/v2/spacing";

import { Tooltip } from "../Tooltip/Tooltip";
import { SHAPE_CONFIG, ToggleItem, ToggleShape } from "./ToggleItem";

type ToggleSingleProps = {
  selected: boolean;
  onChange: (selected: boolean) => void;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  shape?: ToggleShape;
};

type ToggleOption<TValue extends string = string> = {
  value: TValue;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  tooltip?: string;
};

type ToggleMultiProps<TValue extends string> = {
  value: TValue;
  onChange: (value: TValue) => void;
  items: ToggleOption<TValue>[];
  disabled?: boolean;
  shape?: ToggleShape;
};

type ToggleProps<TValue extends string> = ToggleSingleProps | ToggleMultiProps<TValue>;

type SlidingBg = { left: number; width: number; animated: boolean };

function MultiToggle<TValue extends string>({
  value,
  onChange,
  items,
  disabled,
  shape = "rounded",
}: ToggleMultiProps<TValue>) {
  const { containerRadius, itemRadius } = SHAPE_CONFIG[shape];
  const itemRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());
  const [bg, setBg] = useState<SlidingBg | null>(null);

  useLayoutEffect(() => {
    const el = itemRefs.current.get(value);
    if (!el) {
      return;
    }
    setBg((prev) => ({
      left: el.offsetLeft,
      width: el.offsetWidth,
      animated: prev !== null,
    }));
  }, [value]);

  return (
    <Box
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        padding: spacing[0.5],
        borderRadius: containerRadius,
        backgroundColor: colors.bg.elevation.secondary,
      }}
    >
      {bg && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: spacing[0.5],
            bottom: spacing[0.5],
            left: bg.left,
            width: bg.width,
            borderRadius: itemRadius,
            backgroundColor: colors.bg.elevation.primary,
            boxShadow: shadows.xs,
            transition: bg.animated
              ? "left 180ms cubic-bezier(0.4, 0, 0.2, 1), width 180ms cubic-bezier(0.4, 0, 0.2, 1)"
              : "none",
            zIndex: 0,
            pointerEvents: "none",
          }}
        />
      )}
      {items.map((item) => {
        const itemEl = (
          <ToggleItem
            ref={(el) => {
              if (el) {
                itemRefs.current.set(item.value, el);
              } else {
                itemRefs.current.delete(item.value);
              }
            }}
            label={item.label}
            icon={item.icon}
            selected={item.value === value}
            disabled={disabled}
            shape={shape}
            showBg={false}
            onClick={() => onChange(item.value)}
          />
        );
        return item.tooltip ? (
          <Tooltip key={item.value} label={item.tooltip} position="bottom">
            {itemEl}
          </Tooltip>
        ) : (
          <Fragment key={item.value}>{itemEl}</Fragment>
        );
      })}
    </Box>
  );
}

const Toggle = <TValue extends string>(props: ToggleProps<TValue>) => {
  if ("items" in props) {
    return <MultiToggle {...props} />;
  }

  const { selected, onChange, label, icon, disabled, shape = "rounded" } = props;
  return (
    <ToggleItem
      label={label}
      icon={icon}
      selected={selected}
      disabled={disabled}
      shape={shape}
      onClick={() => onChange(!selected)}
    />
  );
};

export { Toggle };
